import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FunnelInsightsApiClient } from '../../api/insightsClient';
import type {
  FunnelInsightsResponse,
  JourneyCohortCensusData,
  JourneyCohortCensusRequest,
  JourneyCensusModelCohortsFamily,
  JourneyCensusSavedCohortsFamily,
  JourneyCensusTwoWayAxis,
  JourneyCensusTwoWayCellsFamily,
} from '../../types';
import { FUNNEL_REPORTS_TEST_IDS } from '../../testIds';

/**
 * J1-I1 — Journey Cohort Census card on the Reports hub: who actually travels
 * this funnel, in three observational families (model cohorts read from
 * audience-calibration artifacts, saved cohorts, source × device × geo 2-way
 * cells). Render-only and consume-only — the server stamps every figure, the
 * model-cohorts family degrades honestly when no calibration artifacts exist,
 * and NOTHING here recomputes or retriggers a run (J1-AC-004: observational
 * language only).
 */

const DEGRADED_MODEL_COHORTS_COPY =
  'No calibration artifacts yet. Model cohorts appear after an audience-calibration run completes for this scope.';

/**
 * B6 (handoff A4): on projects with zero live V2 onboardings the empty state is
 * PERMANENT — a calibration run will not change it, and the copy must say why
 * instead of implying one will.
 */
const STRUCTURAL_MODEL_COHORTS_COPY =
  'This project has no live funnel to scope by, so audience cohorts cannot be built — a calibration run will not change this. Publish a V2 onboarding to enable cohorts.';

/** Read-slice DI so tests inject a reader (AdvancedReadinessPanel idiom). */
export interface JourneyCohortCensusReader {
  getJourneyCohortCensus(
    request: JourneyCohortCensusRequest,
  ): Promise<FunnelInsightsResponse<JourneyCohortCensusData>>;
}

interface JourneyCohortCensusSectionProps {
  projectId: string;
  onboardingId?: string;
  versionId?: string;
  from?: string;
  to?: string;
  client?: JourneyCohortCensusReader;
  /**
   * B6: false = the project has zero live V2 onboardings, so the model-cohorts
   * empty state is structural (permanent), not «not yet». Undefined keeps the
   * transient copy (callers that don't know).
   */
  hasLiveOnboardings?: boolean;
}

function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `$${value.toFixed(2)}`;
}

function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString('en-US');
}

function formatDay(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
}

/** Device-axis coverage note input — rendered in the footer when coverage < 1. */
export function deriveDeviceCoverageNote(axes: JourneyCensusTwoWayAxis[] | undefined): string | null {
  const deviceAxis = axes?.find(axis => axis.axis.includes('device'));
  if (!deviceAxis || deviceAxis.coverage >= 1) return null;
  const pct = `${(deviceAxis.coverage * 100).toFixed(0)}%`;
  return `Device axis coverage ${pct}${deviceAxis.reason ? ` (${deviceAxis.reason})` : ''}.`;
}

export const JourneyCohortCensusSection: React.FC<JourneyCohortCensusSectionProps> = ({
  projectId,
  onboardingId,
  versionId,
  from,
  to,
  client,
  hasLiveOnboardings,
}) => {
  const effectiveClient = useMemo<JourneyCohortCensusReader>(
    () => client ?? new FunnelInsightsApiClient(),
    [client],
  );
  const [data, setData] = useState<JourneyCohortCensusData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!projectId) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    // No calibrationRunId: the hub has none to pass, so model_cohorts arrives
    // in its honest degraded state until a calibration run exists for the scope.
    effectiveClient.getJourneyCohortCensus({
      projectId,
      onboardingId,
      versionId,
      from,
      to,
      families: ['model_cohorts', 'saved_cohorts', 'two_way_cells'],
      signal: controller.signal,
    })
      .then(response => {
        setData(response.data);
        setLoading(false);
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        setData(null);
        setLoading(false);
        setError(err instanceof Error ? err.message : 'Failed to load journey cohort census');
      });
    return () => controller.abort();
  }, [effectiveClient, projectId, onboardingId, versionId, from, to, reloadKey]);

  const deviceCoverageNote = deriveDeviceCoverageNote(
    data?.families.two_way_cells?.axes,
  );

  return (
    <Box
      data-testid={FUNNEL_REPORTS_TEST_IDS.cohortCensus}
      sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 2, bgcolor: 'background.paper' }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Journey Cohort Census</Typography>
        <Typography variant="caption" color="text.secondary">
          Who actually travels this funnel — model cohorts, your saved cohorts, and source × device × geo cells.
        </Typography>
      </Box>

      {loading && <LinearProgress aria-label="Loading journey cohort census" sx={{ my: 1 }} />}

      {error && (
        <Alert
          severity="warning"
          action={<Button color="inherit" size="small" onClick={() => setReloadKey(key => key + 1)}>Retry</Button>}
        >
          Journey cohort census is unavailable: {error}
        </Alert>
      )}

      {data && !error && (
        <Stack spacing={2.5}>
          <ModelCohortsFamilySection family={data.families.model_cohorts} hasLiveOnboardings={hasLiveOnboardings} />
          <SavedCohortsFamilySection family={data.families.saved_cohorts} />
          <TwoWayCellsFamilySection family={data.families.two_way_cells} />
        </Stack>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ mt: 1.75, pt: 1, borderTop: theme => `1px solid ${theme.palette.divider}` }}
      >
        Observational shares — not causal effects.
        {deviceCoverageNote && (
          <span data-testid={FUNNEL_REPORTS_TEST_IDS.cohortCensusCoverage}>{` ${deviceCoverageNote}`}</span>
        )}
      </Typography>
    </Box>
  );
};

function FamilyHeader({ title, children }: { title: string; children?: React.ReactNode }): React.ReactElement {
  return (
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      {children}
    </Stack>
  );
}

function ModelCohortsFamilySection({ family, hasLiveOnboardings }: { family?: JourneyCensusModelCohortsFamily; hasLiveOnboardings?: boolean }): React.ReactElement | null {
  if (!family) return null;

  if (family.state === 'no_calibration_artifacts') {
    return (
      <Box>
        <FamilyHeader title="Model cohorts" />
        <Box
          data-testid={FUNNEL_REPORTS_TEST_IDS.cohortCensusModelDegraded}
          sx={{
            border: theme => `1px dashed ${theme.palette.divider}`,
            borderRadius: 1,
            p: 2,
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2">
            {hasLiveOnboardings === false ? STRUCTURAL_MODEL_COHORTS_COPY : DEGRADED_MODEL_COHORTS_COPY}
          </Typography>
        </Box>
      </Box>
    );
  }

  const { manifest, lineageCoverage, rows } = family;
  return (
    <Box data-testid={FUNNEL_REPORTS_TEST_IDS.cohortCensusModel}>
      <FamilyHeader title="Model cohorts">
        <Chip
          size="small"
          variant="outlined"
          color="success"
          label={`lineage coverage ${formatPercent(lineageCoverage, 0)}`}
        />
      </FamilyHeader>
      {rows.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No model-cohort rows in this window.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cohort (actor posterior)</TableCell>
              <TableCell align="right">Share</TableCell>
              <TableCell align="right">Journeys</TableCell>
              <TableCell align="right">Conversion (shrunk)</TableCell>
              <TableCell align="right">Revenue / user</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.cohortId}>
                <TableCell>{row.name}</TableCell>
                <TableCell align="right">{formatPercent(row.sizeShare, 0)}</TableCell>
                <TableCell align="right">{formatCount(row.journeys)}</TableCell>
                <TableCell align="right">
                  {formatPercent(row.shrunkConversionRate ?? row.conversionRate)}
                  {row.shrunkConversionRate !== null && row.conversionRate !== null && (
                    <Typography variant="caption" color="text.secondary" component="span">
                      {` (raw ${formatPercent(row.conversionRate)})`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{formatMoney(row.revenue)}</TableCell>
                <TableCell align="right">
                  {row.partialLineage
                    ? <Chip size="small" variant="outlined" color="warning" label="partial lineage" />
                    : <Chip size="small" variant="outlined" color="success" label="covered" />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        Read from calibration run {manifest.runId} · {manifest.schema} · pack {manifest.packId} ·{' '}
        {manifest.contentHash.slice(0, 8)}… · emitted {formatDay(manifest.emittedAt)}
      </Typography>
    </Box>
  );
}

function SavedCohortsFamilySection({ family }: { family?: JourneyCensusSavedCohortsFamily }): React.ReactElement | null {
  if (!family) return null;
  return (
    <Box data-testid={FUNNEL_REPORTS_TEST_IDS.cohortCensusSaved}>
      <FamilyHeader title="Saved cohorts" />
      {family.rows.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No saved cohorts yet.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cohort</TableCell>
              <TableCell align="right">Users (est.)</TableCell>
              <TableCell align="right">Conditions</TableCell>
              <TableCell align="right">Size estimated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {family.rows.map(row => (
              <TableRow key={row.cohortId}>
                <TableCell>
                  {row.name}
                  {row.description && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {row.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{formatCount(row.users)}</TableCell>
                <TableCell align="right">{formatCount(row.conditionCount)}</TableCell>
                <TableCell align="right">{formatDay(row.sizeEstimateAsOf)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

function TwoWayCellsFamilySection({ family }: { family?: JourneyCensusTwoWayCellsFamily }): React.ReactElement | null {
  if (!family) return null;
  return (
    <Box data-testid={FUNNEL_REPORTS_TEST_IDS.cohortCensusTwoWay}>
      <FamilyHeader title="Source × Device × Geo (2-way cells, shrunk)" />
      {family.honesty.insufficientOutcome && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
          Not enough outcome events in this window for reliable cell rates
          ({formatCount(family.honesty.outcomeEvents)} observed).
        </Typography>
      )}
      {family.rows.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No qualifying cells in this window.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cell</TableCell>
              <TableCell align="right">Journeys</TableCell>
              <TableCell align="right">Share</TableCell>
              <TableCell align="right">Conversion (shrunk)</TableCell>
              <TableCell align="right">
                <Typography variant="caption" color="text.secondary" component="span">raw</Typography>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {family.rows.map(row => (
              <TableRow key={`${row.axis}-${row.value}-${row.valueB ?? ''}`}>
                <TableCell>{row.valueB ? `${row.value} × ${row.valueB}` : row.value}</TableCell>
                <TableCell align="right">{formatCount(row.n)}</TableCell>
                <TableCell align="right">{formatPercent(row.share, 0)}</TableCell>
                <TableCell align="right">{formatPercent(row.shrunkRate)}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary" component="span">
                    {formatPercent(row.rawRate)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {row.smallN && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="warning"
                      label="small n"
                      data-testid={FUNNEL_REPORTS_TEST_IDS.cohortCensusSmallN}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {family.capped && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          Cross list trimmed to the cell budget ({formatCount(family.capped.cellBudget)} cells).
        </Typography>
      )}
    </Box>
  );
}
